import { $message } from "various-ui";
//* 组件库
import type { UiForm, UiTypes, UiFormItem } from "various-ui";
import { $loading } from "various-ui";
import { ASOForecast } from "@cya/aso-forecast";
//* 组件属性
import type { ModuleFormData, ASOForecastData } from "../index";

export const useComposable = () => {
    //* 插件
    const i18n = useI18n();
    const route = useRoute();

    //* 响应式变量
    const refs = {
        exons: ref<UiTypes.candidate[]>([]),
        transcripts: ref<UiTypes.candidate[]>([]),
        lengths: ref<UiTypes.candidate[]>([
            { label: "15nt", value: "15" },
            { label: "16nt", value: "16" },
            { label: "17nt", value: "17" },
            { label: "18nt", value: "18" },
            { label: "19nt", value: "19" },
            { label: "20nt", value: "20" },
            { label: "21nt", value: "21" },
            { label: "22nt", value: "22" },
            { label: "23nt", value: "23" },
            { label: "24nt", value: "24" },
            { label: "25nt", value: "25" },
            { label: "26nt", value: "26" },
            { label: "27nt", value: "27" },
            { label: "28nt", value: "28" },
            { label: "29nt", value: "29" },
            { label: "30nt", value: "30" },
        ]),

        verifys: reactive({ mutation: true }),
        disableds: reactive({
            form: false, //* 禁止表单提交
            mutation: true, //* 禁止输入突变位点
            canvas: true,
        }),

        mutation: ref<ASOForecastData["data"]["mutation"]>(),
        example: ref<ASOForecast>(),
        name: ref<string>(""),

        max: ref<number>(0),
        min: ref<number>(0),
    };

    //* 表单数据
    const data = reactive<ModuleFormData>({
        id: "",
        name: "",
        task: "skip_exon",
        exon: "",
        length: "20",
        mutation: "",
        transcript: "",
        sites: {
            start: "",
            end: "",
        },
    });

    //* 校验规则
    const rules: Record<string, UiTypes.verifyRule[]> = {
        id: [
            {
                trigger: "blur",
                verify: () => {
                    return { verify: !!data.id, message: `* ${i18n.t("Placeholder.PleaseInputGeneNameCheck")}` };
                },
            },
        ],
    };

    //* 节点
    const nodes = {
        canvas: useTemplateRef<HTMLDivElement>("canvas"),
        form: useTemplateRef<InstanceType<typeof UiForm>>("form"),
        mutation: useTemplateRef<InstanceType<typeof UiFormItem>>("mutation"),
    };

    //* 函数列表
    const methods = {
        //* 基因相关函数
        gene: {
            //* 销毁基因选择的副作用
            destroy: () => {
                data.exon = "";
                data.mutation = "";
                data.transcript = "";
                refs.exons.value = [];
                refs.transcripts.value = [];
                refs.disableds.mutation = true;
            },

            //* 基因选择回调
            change: (response?: UiTypes.candidate) => {
                //* 数据更新
                data.id = response?.id || "";
                data.name = response?.label || "";
                refs.name.value = data.name;
                //* 检测是要清空数据 / 拉取数据
                if (!data.id) methods.gene.destroy();
                else {
                    $loading.open({ message: i18n.t("UnderLoad"), delay: 200 });
                    api.frontend("/api/gene/mrnas-info", {
                        data: { id: data.id },
                        error: () => methods.gene.destroy(),
                        finally: () => $loading.close(),
                        success: (response) => {
                            //* 转录本数据初始化
                            refs.transcripts.value = [];
                            response?.data?.forEach((value: any) =>
                                refs.transcripts.value.push({ label: value.label, value: value.value, data: value })
                            );

                            //* 外显子数据初始化
                            refs.exons.value = [
                                {
                                    label: "",
                                    value: "",
                                    data: null,
                                },
                            ];
                            refs.transcripts.value?.[0]?.data?.exons?.forEach((_: any, index: number) => {
                                const number = index + 1;
                                refs.exons.value.push({
                                    label: `Exon ${number}`,
                                    value: number,
                                    data: _,
                                });
                            });

                            //* 选择状态初始化
                            data.exon = "";
                            data.transcript = refs.transcripts.value?.[0]?.value || "";
                            refs.disableds.mutation = false;

                            //* 绘制可视化图形
                            methods.canvases.render();
                            //* 获取外显子范围坐标
                            methods.getExonRange();
                        },
                    });
                }
            },
        },

        //* 坐标相关函数
        sites: {
            end: () => {
                const end = methods.sites.value(data.sites.end);
                const start = methods.sites.value(data.sites.start);
                if (start == -1 || end == -1) {
                    //* 校验坐标范围
                    if (end > refs.max.value) data.sites.end = `${refs.max.value}`;
                    else if (end < refs.min.value) data.sites.end = `${refs.min.value}`;
                    else data.sites.end = end == -1 ? "" : `${end}`;
                } else {
                    const number = Math.max(end, start);
                    data.sites.end = `${number > refs.max.value ? refs.max.value : number > refs.max.value ? refs.max.value : number}`;
                }
                //* 绘制可视化图形
                if (data.sites.start && data.sites.end) methods.canvases.render();
            },

            start: () => {
                const end = methods.sites.value(data.sites.end);
                const start = methods.sites.value(data.sites.start);
                if (start == -1 || end == -1) {
                    //* 校验坐标范围
                    if (start < refs.min.value) data.sites.start = `${refs.min.value}`;
                    else if (start > refs.max.value) data.sites.start = `${refs.max.value}`;
                    else data.sites.start = start == -1 ? "" : `${start}`;
                } else {
                    const number = Math.min(end, start);
                    data.sites.start = `${number < refs.min.value ? refs.min.value : number < refs.min.value ? refs.min.value : number}`;
                }
                //* 绘制可视化图形
                if (data.sites.start && data.sites.end) methods.canvases.render();
            },

            value: (number: string) => {
                if (number == "") return -1;
                else {
                    const value = Number(number);
                    if (isNaN(value)) return -1;
                    else {
                        return Math.max(Math.floor(value), 0);
                    }
                }
            },
        },

        //* 可视化相关函数
        canvases: {
            //* 渲染函数
            render: (callback?: () => void) => {
                if (!nodes.canvas.value) return callback?.();

                //* 坐标数据初始化
                let exons = {
                    start: "",
                    end: "",
                };
                if (data.exon) {
                    const e = refs.exons.value.find((v) => v.value === data.exon)?.data;
                    exons.start = e[0];
                    exons.end = e[1];
                } else if (data.sites.start && data.sites.end) {
                    exons.start = data.sites.start;
                    exons.end = data.sites.end;
                } else {
                    //* 隐藏可视化图形
                    refs.disableds.canvas = true;
                    return callback?.();
                }

                //* 可视化图形配置
                const configs: ASOForecastData = {
                    resize: true,
                    mode: "Preview",
                    language: (route.meta.routeLanguage as "zh" | "en") || "zh",
                    data: {
                        chr: "X",
                        exon: Number(data.exon),
                        range: { x: { start: Number(exons.start), end: Number(exons.end), size: Number(exons.end) - Number(exons.start) + 1 } },
                        ...(refs.mutation.value && {
                            mutation: {
                                transcript: refs.mutation.value.transcript, //* 转录本名称
                                name: data.name, //* 基因名称
                                site: refs.mutation.value.site, //* 突变位置（起点）
                                str: data.mutation, //* 突变字符串
                                ref: refs.mutation.value.ref, //* 突变前序列
                                alt: refs.mutation.value.alt, //* 突变后序列
                            },
                        }),
                    },
                };

                //* 显示可视化
                refs.disableds.canvas = false;

                if (!refs.example.value) {
                    //* 实例化可视化图形
                    refs.example.value = new ASOForecast(nodes.canvas.value, configs);
                } else {
                    //* 重绘可视化图形
                    refs.example.value?.init(configs);
                }

                //* 回调函数
                callback?.();
            },

            //* 检测是否解析突变字符串函数
            analysis: () => {
                //* 禁用表单提交
                refs.disableds.form = true;
                //* 检测是否进行突变字符串解析
                const keyword = data.mutation?.trim();
                if (!keyword) methods.canvases.render(() => (refs.disableds.form = false));
                else {
                    //* 检测突变信息是否合法
                    api.frontend("/api/mutations/analyze2", {
                        hidden: true,
                        method: "POST",
                        data: {
                            id: data.id,
                            keyword: data.mutation,
                            transcript: data.transcript,
                        },
                        error: (error) => nodes.mutation.value?.show("* " + error.message || i18n.t("IncorrectMutationFormat"), "error"),
                        finally: () => $loading.close(),
                        success: (response) => {
                            //* 记录突变数据
                            refs.mutation.value = response || {};
                            //* 渲染函数
                            methods.canvases.render(() => (refs.disableds.form = false));
                        },
                    });
                }
            },
        },

        //* 获取外显子范围坐标
        getExonRange: () => {
            //* 切换转录本重置目标外显子内容
            data.sites.start = "";
            data.sites.end = "";
            data.exon = "";
            methods.canvases.render();
            if (!data.transcript) return;
            const transcript = refs.transcripts.value.find((v) => v.value === data.transcript)?.data || [];
            if (!transcript) return;
            //* 获取外显子范围坐标
            refs.min.value = transcript?.exons[0][1] + 1;
            refs.max.value = transcript?.exons.slice(-1)[0][0] - 1;
        },

        //* 重置函数
        reset: () => {
            //* 数据清空
            nodes.form.value?.reset();
            methods.gene.destroy();
            data.id = "";
            data.name = "";
            data.length = "20";
            data.sites = {
                start: "",
                end: "",
            };

            //* 隐藏可视化图形
            refs.disableds.canvas = true;
        },

        //* 表单提交函数
        submit: () => {
            nodes.form.value?.validator((result) => {
                if (!result) return;
                if (!data.exon && data.sites.start && data.sites.end) {
                    if (parseInt(data.sites.end) - parseInt(data.sites.start) > 500) {
                        $message.warning(i18n.t("Placeholder.PleaseInputExonCheck"));
                        return;
                    }
                }
                //*请求接口上传数据
                api.frontend("/api/bio/aso", {
                    method: "POST",
                    data: data,
                    success: (id) => {
                        window.open(routerResolve({ name: "ASOExperimentResult", params: { id: id } }).href, "_blank");
                    },
                });
            });
        },
    };

    //* 计算属性
    const computeds = {
        //* 外显子编号候选列表
        exonCandidates: computed(() => {
            if (!data.transcript) return [];
            const index = refs.transcripts.value.findIndex((v) => v.value === data.transcript);
            if (index === -1) return [];
            const exons: {
                label: string;
                value: any;
                data: any;
            }[] = [
                {
                    label: "",
                    value: "",
                    data: null,
                },
            ];

            refs.transcripts.value?.[index]?.data?.exons?.forEach((_: any, index: number) => {
                const number = index + 1;
                exons.push({
                    label: `Exon ${number}`,
                    value: number,
                    data: _,
                });
            });
            return exons;
        }),
        //* 检测是否选择基因
        usable: computed(() => !!data.id),
        //* 检测是否可以提交
        submit: computed(() => !!data.exon || (!!data.sites.start && !!data.sites.end)),
    };

    return { refs, data, rules, methods, computeds };
};
